"use client";

import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import {
  findPhoneCountry,
  getPhoneCountries,
  phoneCountryFlagSrc,
  type PhoneCountry,
} from "@/lib/phone-countries";
import { cn } from "@/lib/utils";
import { useContent } from "@/components/site/ContentProvider";
import type { CountryCode } from "libphonenumber-js";

function CountryFlag({ iso, className }: { iso: string; className?: string }) {
  const src = phoneCountryFlagSrc(iso, 40);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      width={20}
      height={15}
      loading="lazy"
      decoding="async"
      className={cn("phone_country_flag", className)}
      aria-hidden
      onError={(e) => {
        e.currentTarget.style.visibility = "hidden";
      }}
    />
  );
}

/**
 * Full-world calling-code picker — custom panel (BeUI Select staggers every
 * item; 240+ countries would crawl). Trigger shows flag + dial only.
 * Panel portals to body so #contact / .budget_set overflow cannot clip it.
 */
export function CountryCodeSelect({
  name = "phoneCountry",
  value,
  onCountryChange,
  className,
}: {
  name?: string;
  value: CountryCode;
  onCountryChange?: (iso: CountryCode) => void;
  className?: string;
}) {
  const site = useContent();
  const countries = useMemo(
    () => getPhoneCountries(site.locale),
    [site.locale],
  );
  const [open, setOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const [mounted, setMounted] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const current: PhoneCountry =
    findPhoneCountry(value, site.locale) ?? countries[0] ?? {
      iso: "EG" as CountryCode,
      dial: "+20",
      name: "Egypt",
    };

  useEffect(() => {
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 6;
      /* Roomier option rows need a taller panel so ~8–10 items stay visible */
      const maxH = Math.min(window.innerHeight * 0.55, 26 * 16);
      const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
      const spaceAbove = rect.top - gap - 8;
      const placeTop =
        spaceBelow < Math.min(maxH, 12 * 16) && spaceAbove > spaceBelow;
      const height = Math.min(maxH, Math.max(placeTop ? spaceAbove : spaceBelow, 10 * 16));
      const width = Math.min(18 * 16, Math.max(rect.width, window.innerWidth * 0.7));
      const left = Math.min(
        Math.max(8, rect.left),
        window.innerWidth - width - 8,
      );

      setPanelStyle({
        position: "fixed",
        left,
        width,
        maxHeight: height,
        zIndex: 10050,
        ...(placeTop
          ? { bottom: window.innerHeight - rect.top + gap, top: "auto" }
          : { top: rect.bottom + gap, bottom: "auto" }),
      });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function pick(iso: CountryCode) {
    onCountryChange?.(iso);
    setOpen(false);
  }

  function onTriggerKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  const panel =
    open && mounted
      ? createPortal(
          <div
            ref={panelRef}
            id={listId}
            role="listbox"
            aria-label={site.aria.countryCodes}
            style={panelStyle}
            className={cn(
              "budget_currency_panel phone_country_panel",
              "flex flex-col overflow-hidden rounded-none border border-white/20 bg-[#080808] text-white shadow-none",
            )}
            data-native-scroll=""
          >
            <ul className="phone_country_list m-0 max-h-none flex-1 list-none overflow-y-auto p-0">
              {countries.map((country) => {
                const selected = country.iso === value;
                return (
                  <li key={country.iso} className="m-0 p-0">
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={cn(
                        "phone_country_option flex w-full items-center gap-2.5 px-3.5 py-3 text-start text-[1em] normal-case tracking-normal text-white/70",
                        "hover:bg-white/10 hover:text-white",
                        selected && "bg-white/10 text-white",
                      )}
                      onClick={() => pick(country.iso)}
                    >
                      <CountryFlag iso={country.iso} />
                      <span className="min-w-0 flex-1 truncate">{country.name}</span>
                      <span className="shrink-0 text-white/45" dir="ltr">
                        {country.dial}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={cn("budget_currency phone_country", className)}
      dir="ltr"
    >
      <input type="hidden" name={name} value={value} />
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          "budget_currency_trigger",
          "flex h-[2.25em] min-w-[5.75em] items-center justify-between gap-2 rounded-none border border-white/20 bg-transparent px-3 py-0",
          "text-[0.875em] uppercase tracking-[0.06em] text-white shadow-none",
          "hover:border-white/45",
          open && "border-white/45",
        )}
        aria-label={`${site.aria.countryCodes}, ${current.name} ${current.dial}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
      >
        <span className="flex items-center gap-2 normal-case tracking-normal">
          <CountryFlag iso={current.iso} />
          <span className="label small text-white" dir="ltr">
            {current.dial}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 opacity-70 transition-transform",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {panel}
    </div>
  );
}
